/**
 * @fileoverview Error Page Component
 *
 * This file implements the error page displayed when a user doesn't have any client associated
 * with their account. It provides a way for users to log out of the application when they 
 * encounter this error state.
 *
 * Key features:
 * - Clear error messaging about the missing client association
 * - Logout functionality with Okta authentication
 * - Consistent branding through Header and Footer components
 * - Client-specific styling using the global state
 */

import React, { useContext, useState } from "react";
import {
  Button,
  ButtonVariant,
  IconName,
  ButtonIconAlign,
} from "@eightfold.ai/octuple";
import Header from "../commons/Header";
import { secondaryButtonStyle } from "../commons/commonStyles";
import Footer from "../commons/Footer";
import { globalContext } from "../store";
import { getOnboardingImg, getHeaderLogo } from "../commons/commonFunctions";
import { useOktaAuth } from "@okta/okta-react";

/**
 * ErrorPage Component
 * 
 * Displays a user-friendly error message when a user doesn't have any client associated
 * with their account. Provides a logout button to exit the application.
 * 
 * @returns {JSX.Element} A formatted error page with message and logout button
 */
const ErrorPage = () => {
  const { oktaAuth } = useOktaAuth();
  const { globalState } = useContext(globalContext);
  const [showLoader, setShowLoader] = useState(false);
  const title = globalState?.clientData?.name;

  /**
   * Handle logout button click
   * Clears session storage and triggers Okta sign out process
   * Shows a loading spinner during the logout process
   */
  const logOutClick = () => {
    setShowLoader(true);
    sessionStorage.clear();
    if (oktaAuth) {
      oktaAuth.signOut({
        postLogoutRedirectUri: window.location.origin,
      });
    }
  };
  return (
    <>
      <div className="mainWrapper">
        {/* Left side image panel with client-specific background */}
        <div
          className="imgWrapper"
          style={{
            background: `url(${getOnboardingImg(globalState)}) no-repeat`,
          }}
        >
          &nbsp;
        </div>
        {/* Right side content panel with error message and logout button */}
        <div className="rightWrapper">
          <Header title={title} headerLogo={getHeaderLogo(globalState)} />
          <div className="contentWrapper" style={{ alignItems: 'center' }}>
            <h1 className="titleStyle" >You don't have any client associated</h1>
            <div style={{ marginTop: '10px' }}>
              <div>
                {/* Logout button with loading state indicator */}
                <Button
                  text="Log Out"
                  variant={ButtonVariant.Secondary}
                  classNames="secondaryButton"
                  style={secondaryButtonStyle}
                  onClick={() => {
                    logOutClick();
                  }}
                  iconProps={
                    showLoader
                      ? { path: IconName.mdiLoading, spin: true }
                      : undefined
                  }
                  alignIcon={ButtonIconAlign.Right}
                  disabled={showLoader}
                />
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
};

/**
 * Export the ErrorPage component as the default export
 * This allows it to be imported using:
 * import ErrorPage from './pages/ErrorPage';
 */
export default ErrorPage;
